import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Box, Paper, Input, Spinner, Text } from '@/components/primitives';
import { ActionButton } from '@/components/common/ActionButton';
import { StyledDialog } from '@/components/dialogs/StyledDialog';
import {
  sendMergeVerification,
  executeMerge,
  ignoreMerge,
  getMe,
  type MergeSuggestion,
} from '@/api/auth';
import { isContentGrant } from '@/api/mergeDenial';
import { parseMergeSendRateLimit } from '@/api/mergeSendRefusal';
import type { DenialMergeSuggestion, DenialCode } from '@/api/mergeDenial';
import type { User } from '@/types';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { signIn, updateCurrentUser } from '@/auth/authData';
import { queryClient } from '@/api/queryClient';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { reportMutationError } from '@/api/reportMutationError';
import { testIds } from '@/test/testIds';
import { isEmail } from '@/utils/contactMethodUtils';
import { obfuscateContactMethod } from '@/utils/obfuscateContactMethod';
import { dialogMessages, formMessages, retryInPhrase } from '@/constants';
import { useMergeSuggestionStore } from '@/store/useMergeSuggestionStore';
import { Icons, Actions, vars, transitions } from '@/theme';

/**
 * AccountMergeDialog — the single UI for user-account merge suggestions.
 *
 * Two variants:
 *
 * The PROACTIVE variant is dismissible (#223 defect B/C). It used to be a
 * forced binary — no X, no Escape, no overlay click — on the theory that
 * users don't understand why merging matters until access bites. In practice
 * the person whose other account's only method is dead (the Zach case) had
 * exactly one exit, Ignore, and when the suggestion row was already gone that
 * exit 404ed: nine `/users/merge/{id}/ignore/` 404s in thirty seconds against
 * an unclosable modal. So: it closes, a dismissal is remembered for the
 * session, a 404 from ignore/verify ends the interaction instead of leaving
 * it open to be retried, and the copy names the real resolver (support).
 * The DENIAL variant stays forced — dismissing in place would strand the user
 * on a page that cannot load; its exit is Go Back, which navigates.
 *
 * - `proactive` (login / auth-me refresh): **Ignore** or **Confirm** (pick a
 *   contact method). Ignore only suppresses this proactive channel — the
 *   backend re-delivers ignored suggestions at denial time, so a wrong
 *   Ignore self-corrects at the next access wall.
 * - `denial` (403 with a merge_suggestion envelope): **Go Back** or
 *   **Confirm ownership**. No Ignore here: denial-time suggestions may
 *   already BE ignored, and dismissing in place would strand the user on a
 *   page that cannot load.
 *
 * The verify step's Cancel returns to the fork (the code may have gone to a
 * phone the user doesn't have in hand) — never out of the dialog.
 *
 * Mechanics preserved from the retired MergeSuggestionBanner: send-code per
 * exact method value; execute returns HTTP 200 even for a wrong code, so we
 * branch on `body.success` and keep the dialog open on rejection; merge
 * success reloads /auth/me re-attaching the token (which /me omits).
 * New here: success also invalidates the entire query cache — the user's
 * identity/memberships/level changed, and for the denial variant this IS the
 * "retry the denied request" (the failed route queries refetch).
 *
 * Feedback ownership (#821): a dialog component's own `useFeedbackZone()`
 * resolves to its PARENT layer (StyledDialog's layer provider is in its render
 * output). So the outer component holds only `pageZone`, for outcomes that
 * close the dialog (merged, ignored), and `AccountMergeBody`, rendered inside
 * the StyledDialog, owns the send/execute/ignore mutations: every error while
 * the dialog is open lands in this dialog's own band.
 */

interface AccountMergeDialogBaseProps {
  /** The merge resolved (merge executed or suggestion ignored). */
  onResolved?: () => void;
}

interface ProactiveProps extends AccountMergeDialogBaseProps {
  variant: 'proactive';
  suggestion: MergeSuggestion;
}

interface DenialProps extends AccountMergeDialogBaseProps {
  variant: 'denial';
  suggestion: DenialMergeSuggestion;
  code: DenialCode | null;
  /** Name of the context the denial happened in (the user's current context). */
  contextName: string | null;
  /** Leave the place they can't be (history back / default route). */
  onGoBack: () => void;
}

export type AccountMergeDialogProps = ProactiveProps | DenialProps;

type Step = 'fork' | 'verify';

export function AccountMergeDialog(props: AccountMergeDialogProps) {
  const { suggestion, onResolved } = props;
  const dismissSuggestion = useMergeSuggestionStore((s) => s.dismissSuggestion);
  // Resolved OUTSIDE the StyledDialog → the page/global zone, which survives
  // this dialog closing.
  const pageZone = useFeedbackZone();
  // Lifted from the body: a request in flight blocks closing.
  const [busy, setBusy] = useState(false);
  const messages = dialogMessages.accountMerge;

  /**
   * End this suggestion for the session: the host must not re-open it, and
   * /auth/me's copy must stop driving it. Used by Ignore-success, by the
   * user's own dismiss, and by a 404 (the row is gone server-side).
   */
  const finishSuggestion = () => {
    dismissSuggestion(suggestion.id);
    updateCurrentUser({ mergeSuggestion: undefined } as unknown as Partial<User>);
    onResolved?.();
  };

  // Outcomes that close the dialog: push to the surviving zone FIRST, then close.
  const handleMerged = () => {
    pageZone.push(messages.mergeSuccess, 'success');
    dismissSuggestion(suggestion.id);
    onResolved?.();
  };

  const handleIgnored = () => {
    pageZone.push(messages.ignoreSuccess, 'success');
    // Re-ignoring must not re-pop this session: /auth/me can still carry
    // the row (and the zero-contact resurfacing rule deliberately brings
    // ignored ones back), so clearing the user copy alone is not enough.
    finishSuggestion();
  };

  return (
    <StyledDialog
      open
      // Denial stays a forced binary (its exit is Go Back, which navigates).
      // Proactive is dismissible (#223 defect B): closing it records the
      // dismissal for the session so it cannot immediately re-pop.
      hideClose={props.variant === 'denial'}
      closeDisabled={props.variant === 'denial' || busy}
      onClose={props.variant === 'denial' ? undefined : finishSuggestion}
      size="compact"
      title={messages.title}
      titleIcon={<Icons.merge style={{ color: vars.color.success.base }} />}
      // The actions live in AccountMergeBody (inside the dialog layer) so the
      // mutations they drive answer in this dialog's own band (#821).
      footer={null}
      DialogProps={{ 'data-testid': testIds.components.accountMerge.dialog }}
    >
      <AccountMergeBody
        dialogProps={props}
        onGone={finishSuggestion}
        onMerged={handleMerged}
        onIgnored={handleIgnored}
        onBusyChange={setBusy}
      />
    </StyledDialog>
  );
}

/**
 * Rendered INSIDE the StyledDialog, so `useApiMutation` / `useFeedbackZone`
 * here resolve to the merge dialog's own band.
 */
function AccountMergeBody({
  dialogProps: props,
  onGone,
  onMerged,
  onIgnored,
  onBusyChange,
}: {
  dialogProps: AccountMergeDialogProps;
  /** The suggestion no longer exists server-side: end it silently. */
  onGone: () => void;
  /** The merge executed (identity refreshed, queries invalidated). */
  onMerged: () => void;
  /** Ignore succeeded. */
  onIgnored: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { suggestion } = props;
  const [step, setStep] = useState<Step>('fork');
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  // #1163 addendum: the code step was reached through a 429 (nothing sent), so
  // its line must not claim a code was sent.
  const [sendRefused, setSendRefused] = useState(false);
  const zone = useFeedbackZone();
  const { user } = useCurrentUser();
  const messages = dialogMessages.accountMerge;

  /**
   * A 404 means the suggestion no longer exists — merged, ignored elsewhere,
   * or cleaned up. Retrying can only 404 again, so stop: close the dialog and
   * remember the id. Returns true when it handled the error.
   */
  const handleGone = (error: unknown): boolean => {
    if (!(error instanceof AxiosError) || error.response?.status !== 404) return false;
    onGone();
    return true;
  };

  const contactMethods = suggestion.verifyMethods.map((method) => ({
    type: isEmail(method) ? ('email' as const) : ('phone' as const),
    value: method,
    display: obfuscateContactMethod(method),
  }));

  const sendVerificationMutation = useApiMutation({
    mutationFn: (method: string) =>
      sendMergeVerification(suggestion.id, { verificationMethod: method }),
    // A 200 always moves to the code step: a fresh send and a press inside the
    // cooldown (nothing sent, the earlier code stays valid) answer the same.
    // `retry_after` is typed but not rendered (no countdown, #1163 scope).
    onSuccess: () => {
      setSendRefused(false);
      setStep('verify');
    },
    // Supplying onError REPLACES useApiMutation's default handling, so the
    // ordinary-failure branch is spelled out.
    onError: (error) => {
      // #1163 (vingapi#1666 S2): a spent send budget is not a dead end. Nothing
      // was sent, but a code an earlier press sent still works: show the code
      // step for the picked method and say when another can be sent.
      const rateLimit = parseMergeSendRateLimit(error);
      if (rateLimit) {
        setSendRefused(true);
        setStep('verify');
        reportMutationError(zone, error, {
          message: messages.sendRateLimited(retryInPhrase(rateLimit.retryAfterSeconds)),
          severity: 'warning',
        });
        return;
      }
      if (handleGone(error)) return;
      reportMutationError(zone, error, { message: messages.sendFailed });
    },
  });

  const executeMergeMutation = useApiMutation({
    mutationFn: (code: string) => executeMerge(suggestion.id, { code }),
    // The endpoint returns 200 even for a wrong code (success:false) — branch
    // on the body, not the HTTP status. successMessage is intentionally NOT
    // set here because it would fire on any 2xx, including a rejected code.
    // The wrong-code push carries this mutation's key by hand (#1628-c3): the
    // ambient key is unset after the first await, and zone.push never reads it.
    // The wrapper's clear on 2xx then removes it when a right code lands.
    onSuccess: async (data): Promise<void> => {
      if (!data?.success) {
        zone.push(data?.error || messages.verifyFailed, 'error', { source: executeMergeMutation.feedbackSource });
        return; // keep the dialog open so the user can retry
      }
      // A merge changes the user's identity (org memberships, contact
      // methods, level, and the suggestion itself). Reload /auth/me and
      // write it back, re-attaching the token which /me omits (audit H4).
      const token = user?.token;
      let switched = false;
      if (token) {
        try {
          const refreshed = await getMe(token);
          switched = user !== null && refreshed.id !== user.id;
          signIn({ ...refreshed, token });
        } catch {
          // Non-fatal: the merge succeeded; a failed refresh just leaves
          // stale user data until the next refetch reloads it.
        }
      }
      // Everything server-derived may have changed with the new identity.
      // For the denial variant this is the retry: the denied route queries
      // refetch and the page the user was headed to loads. When /me came back
      // as the other login (the requester can be the duplicate, vingapi#1858)
      // the user-switch already refreshed every active query once (#1336):
      // a second blanket invalidate would fetch each of them twice.
      if (!switched) await queryClient.invalidateQueries();
      onMerged();
    },
    onError: (error) => {
      if (handleGone(error)) return;
      reportMutationError(zone, error, { message: messages.verifyFailed });
    },
  });

  const ignoreMergeMutation = useApiMutation({
    mutationFn: () => ignoreMerge(suggestion.id),
    onSuccess: () => {
      onIgnored();
    },
    // A 404 here is the exact defect-C loop: the row is already gone, so the
    // dismissal has effectively happened. Treat it as done, not as a failure
    // to retry against.
    onError: (error) => {
      if (handleGone(error)) return;
      reportMutationError(zone, error, { message: messages.ignoreFailed });
    },
  });

  const isLoading =
    sendVerificationMutation.isPending ||
    executeMergeMutation.isPending ||
    ignoreMergeMutation.isPending;
  useEffect(() => {
    onBusyChange(isLoading);
    // A forced unmount mid-request must not leave the outer dialog locked.
    return () => onBusyChange(false);
  }, [isLoading, onBusyChange]);

  const handleSelectMethod = (method: string) => {
    setSelectedMethod(method);
    sendVerificationMutation.mutate(method);
  };

  const handleVerify = () => {
    const trimmed = verificationCode.trim();
    if (trimmed) {
      executeMergeMutation.mutate(trimmed);
    }
  };

  const intro =
    props.variant === 'denial'
      ? // A content grant names a share, not a level — the consume gate's
        // denial is not about a seat the other account holds (it holds none
        // to inherit), but about the org's assignment of this exact share.
        isContentGrant(props.suggestion.grants)
        ? messages.denialContentIntro(props.suggestion.otherUser.displayName)
        : messages.denialIntro(
            props.suggestion.otherUser.displayName,
            props.suggestion.grants.level,
            props.contextName,
          )
      : messages.proactiveIntro(
          contactMethods.length,
          contactMethods[0]?.type === 'phone' ? 'phone' : 'email',
        );

  const selectedIsEmail = selectedMethod ? isEmail(selectedMethod) : true;

  const forkFooter = (
    <Box display="flex" style={{ justifyContent: 'center', width: '100%' }}>
      {props.variant === 'denial' ? (
        <ActionButton
          action={Actions.cancel}
          mode="dialog-secondary"
          label={messages.goBack}
          onClick={props.onGoBack}
          disabled={isLoading}
          testIdOverride={testIds.components.accountMerge.goBackButton}
          style={{ width: 150 }}
        />
      ) : (
        <ActionButton
          action={Actions.cancel}
          mode="dialog-secondary"
          label={messages.ignore}
          onClick={() => ignoreMergeMutation.mutate()}
          disabled={isLoading}
          loading={ignoreMergeMutation.isPending}
          loadingLabel={messages.ignore}
          testIdOverride={testIds.components.accountMerge.ignoreButton}
          style={{ width: 150 }}
        />
      )}
    </Box>
  );

  const verifyFooter = (
    <Box display="flex" style={{ justifyContent: 'center', gap: 8, width: '100%' }}>
      <ActionButton
        action={Actions.cancel}
        mode="dialog-secondary"
        label={messages.cancel}
        onClick={() => {
          // Back to the fork — never out of the dialog. The code may have
          // gone somewhere the user can't reach right now; they can pick
          // another method or take the other path.
          setVerificationCode('');
          setSelectedMethod(null);
          setSendRefused(false);
          setStep('fork');
        }}
        disabled={isLoading}
        testIdOverride={testIds.components.accountMerge.cancelButton}
      />
      <ActionButton
        action={Actions.verify}
        mode="dialog-primary"
        label={messages.verify}
        onClick={handleVerify}
        disabled={!verificationCode.trim()}
        loading={executeMergeMutation.isPending}
        loadingLabel={messages.verify}
        testIdOverride={testIds.components.accountMerge.verifyButton}
      />
    </Box>
  );

  return (
    <>
      {step === 'fork' ? (
        <Box style={{ textAlign: 'center' }}>
          <Text color="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {intro}
          </Text>
          <Box
            display="flex"
            style={{ flexDirection: 'column', gap: '8px', alignItems: 'center' }}
          >
            {contactMethods.map((method) => (
              <Paper
                key={method.value}
                data-testid={testIds.components.accountMerge.methodCard}
                onClick={() => !isLoading && handleSelectMethod(method.value)}
                style={{
                  width: 275,
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  borderColor: vars.color.success.dark,
                  backgroundColor: vars.color.success.tint,
                  color: vars.color.success.dark,
                  transition: transitions.all,
                }}
              >
                {method.type === 'email' ? (
                  <Icons.email style={{ fontSize: 20 }} />
                ) : (
                  <Icons.sms style={{ fontSize: 20 }} />
                )}
                <Text style={{ fontWeight: 500 }}>{method.display}</Text>
                {sendVerificationMutation.isPending && selectedMethod === method.value && (
                  <Spinner size={16} style={{ marginLeft: 'auto' }} />
                )}
              </Paper>
            ))}
          </Box>
        </Box>
      ) : (
        <Box>
          <Text color="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {sendRefused ? messages.codePrompt : messages.codeSent(selectedIsEmail ? 'email' : 'SMS')}
          </Text>
          <Input
            label="Verification Code"
            placeholder="Enter Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            disabled={isLoading}
            autoFocus
            data-testid={testIds.components.accountMerge.codeInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && verificationCode.trim()) {
                handleVerify();
              }
            }}
          />
        </Box>
      )}

      {/*
        #223 defect E: the code goes to the OTHER account's verified method,
        which can be a phone the person lost years ago — in which case no
        amount of retrying helps and staff are the only resolver. Same
        mailto convention AccessDenied and SignUp already use; no new
        component and no new support channel.
      */}
      <Text
        variant="caption"
        color="secondary"
        style={{ display: 'block', marginTop: 24, textAlign: 'center' }}
      >
        {messages.cannotReceiveCodePrefix}{' '}
        <a href={`mailto:${formMessages.consume.supportEmail}`}>
          {messages.contactSupport}
        </a>
      </Text>

      <Box style={{ marginTop: vars.spacing.lg }}>
        {step === 'fork' ? forkFooter : verifyFooter}
      </Box>
    </>
  );
}
